import { makeAutoObservable, runInAction } from 'mobx';
import { ConsentCategory, ProviderKey, isProviderKey } from './cookieCatalogue';
import { ConsentConfig, parseConfig, applyStyleVars } from './consentConfig';
import { DEFAULT_TEXTS } from './consentTexts';
import { StoredConsent, readConsent, writeConsent, needsPrompt, newConsentId, deleteTrackingCookies } from './consentStorage';
import { consentAxios } from './consentApi';

export type ConsentAction = 'accept_all' | 'necessary_only' | 'selected' | 'withdraw';

export interface CookieConsentBlock {
  enabled: boolean;
  eventId: number;
  config: string | null;
  providersInUse: string[];
}

interface Pending {
  key: string;
  category: ConsentCategory;
  run: () => void;
}

class ConsentStore {
  initialised = false;
  enabled = false;
  eventId = 0;
  lang = 'swe';
  suppressUi = false;
  config: ConsentConfig = parseConfig(null);
  providersInUse: ProviderKey[] = ['jirango'];
  /** The active choice. null = no valid choice: only necessary items run. */
  choice: StoredConsent | null = null;
  /** Last stored choice, even if outdated; used to prefill toggles and keep the consent id. */
  previous: StoredConsent | null = null;
  dialogOpen = false;
  labels: Record<string, string> = {};

  private pending: Pending[] = [];
  private ran = new Set<string>();

  constructor() {
    makeAutoObservable(this, { pending: false, ran: false, clickHandlerInstalled: false } as any);
  }

  init(block: CookieConsentBlock | null | undefined, lang: string, suppressUi: boolean) {
    const eventId = block?.eventId ?? 0;
    if (this.initialised && this.eventId === eventId) return;
    this.initialised = true;
    this.eventId = eventId;
    this.lang = lang || 'swe';
    this.suppressUi = suppressUi;
    this.enabled = !!block?.enabled;

    if (!this.enabled) {
      this.flush();
      return;
    }

    this.config = parseConfig(block.config);
    const providers = (block.providersInUse ?? []).filter(isProviderKey);
    this.providersInUse = providers.length ? providers : ['jirango'];

    const stored = readConsent(this.eventId);
    this.previous = stored;
    this.choice = needsPrompt(stored, this.config.version, this.providersInUse) ? null : stored;
    this.dialogOpen = !this.choice && !this.suppressUi;
    deleteTrackingCookies(this.choice?.categories ?? ['necessary']);

    applyStyleVars(this.config.style);
    this.installClickHandler();
    this.loadLabels();
    this.flush();
  }

  get uiVisible() {
    return this.enabled && !this.suppressUi;
  }

  get privacyUrl() {
    return this.config.privacyUrl?.[this.lang] || '';
  }

  hasConsent(category: ConsentCategory) {
    if (!this.enabled || category === 'necessary') return true;
    return !!this.choice?.categories.includes(category);
  }

  /** Runs `run` once, now or as soon as `category` is accepted. `key` prevents double runs (e.g. GA injected twice). */
  whenAllowed(key: string, category: ConsentCategory, run: () => void) {
    if (this.ran.has(key)) return;
    if (this.hasConsent(category)) {
      this.ran.add(key);
      run();
    } else if (!this.pending.some(p => p.key === key)) {
      this.pending.push({ key, category, run });
    }
  }

  t(key: string): string {
    const override = this.config.texts?.[this.lang]?.[key];
    if (override && override.trim()) return override;
    const label = this.labels[key.toUpperCase()];
    if (label && label !== key && label !== `*${key}*` && label !== `#${key}#`) return label;
    return DEFAULT_TEXTS[key] ?? key;
  }

  openDialog() {
    if (this.uiVisible) this.dialogOpen = true;
  }

  /** Only possible once a choice exists; closing without a choice is not consent. */
  closeDialog() {
    if (this.choice) this.dialogOpen = false;
  }

  async save(selected: ConsentCategory[], action: ConsentAction) {
    const categories = Array.from(new Set<ConsentCategory>(['necessary', ...selected]));
    const before = this.choice;
    const revoked = !!before && before.categories.some(c => !categories.includes(c));
    const stored: StoredConsent = {
      consentId: before?.consentId ?? this.previous?.consentId ?? newConsentId(),
      version: this.config.version,
      categories,
      providers: [...this.providersInUse],
      timestamp: Date.now(),
    };
    writeConsent(this.eventId, stored);
    runInAction(() => {
      this.choice = stored;
      this.previous = stored;
      this.dialogOpen = false;
    });

    try {
      await consentAxios.post('./cookieconsent/log', {
        consentId: stored.consentId,
        eventId: this.eventId,
        categories,
        providers: stored.providers,
        version: stored.version,
        action: revoked ? 'withdraw' : action,
      });
    } catch (e) {
      console.error('Cookie consent log failed', e);
    }

    if (revoked) {
      deleteTrackingCookies(categories);
      window.location.reload();
      return;
    }
    this.flush();
  }

  private flush() {
    const still: Pending[] = [];
    for (const p of this.pending) {
      if (this.ran.has(p.key)) continue;
      if (this.hasConsent(p.category)) {
        this.ran.add(p.key);
        p.run();
      } else {
        still.push(p);
      }
    }
    this.pending = still;
  }

  private clickHandlerInstalled = false;
  private installClickHandler() {
    if (this.clickHandlerInstalled) return;
    this.clickHandlerInstalled = true;
    // Placeholders inside rich text (consentHtml.ts) are plain HTML: one delegated listener opens the dialog.
    document.addEventListener('click', e => {
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('[data-cc-open]')) {
        e.preventDefault();
        this.openDialog();
      }
    });
  }

  private async loadLabels() {
    try {
      const { data } = await consentAxios.get('./systemLabel/group/', {
        params: { groupName: 'CMS.CookieConsent', languageCode: this.lang },
      });
      const map: Record<string, string> = {};
      (Array.isArray(data) ? data : []).forEach((l: any) => {
        if (l?.name && typeof l.value === 'string') map[String(l.name).toUpperCase()] = l.value;
      });
      runInAction(() => {
        this.labels = map;
      });
    } catch (e) {
      console.error('Cookie consent labels failed', e);
    }
  }
}

export const consentStore = new ConsentStore();
